/* t14 diagnostic only: observe the legacy cart-upsell submission boundary.
   No cart mutation, retry, navigation, or external logging is performed. */
(() => {
  if (window.LegacyUpsellDebug) return;

  const VERSION = 't14-legacy-upsell-debug-1.0.0';
  const LIMIT = 800;
  const BODY_LIMIT = 262144;
  let active = false;
  let session = 0;
  let sequence = 0;
  let discarded = 0;
  let records = [];
  let pending = new Map();
  let cleanup = [];
  let observer;
  let scanTimer;
  let seen = new Set();
  let originalFetch;
  let fetchWrapper;
  let xhrOpen;
  let xhrSend;
  let openWrapper;
  let sendWrapper;
  const xhrRequests = new WeakMap();

  const time = () => Math.round(performance.now());
  const safe = (fn, fallback) => { try { return fn(); } catch (_) { return fallback; } };
  const log = (type, detail = {}, level = 'info') => {
    if (!active) return;
    records.push({ n: ++sequence, t: time(), type, level, ...detail });
    if (records.length > LIMIT) { records.shift(); discarded += 1; }
  };
  const once = (key, type, detail, level = 'warn') => {
    if (seen.has(key)) return;
    seen.add(key);
    log(type, detail, level);
  };

  const route = (input) => {
    const value = typeof input === 'string' || input instanceof URL ? input : input?.url;
    let url;
    try { url = new URL(value, location.href); } catch (_) { return { kind: 'unknown' }; }
    const path = url.pathname;
    const sameOrigin = url.origin === location.origin;
    const match = path.match(/\/cart\/(add|change|update|clear)(?:\.js|\.json)?$/);
    const kind = match ? `cart:${match[1]}` : /\/cart(?:\.js|\.json)?$/.test(path) ? 'cart:read' : 'other';
    return { kind, sameOrigin, host: sameOrigin ? 'same-origin' : url.hostname };
  };

  const markerKeys = new Set(['_theme_upsell_key', '_theme_gift_key', '_theme_additional_key', '_db_addon']);
  const itemMarker = (item) => {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') return [];
    return Object.keys(properties).filter((key) => markerKeys.has(key) && properties[key] !== '');
  };
  const configuredVariants = () => new Set(
    [...document.querySelectorAll('cart-drawer-upsell[data-id]')]
      .map((node) => String(node.dataset.id || ''))
      .filter(Boolean),
  );
  const responseSummary = (body) => {
    if (!body || typeof body !== 'object') return { shape: 'non-object' };
    if (!Array.isArray(body.items)) return { shape: body.status || body.errors ? 'api-error' : 'object', status: body.status || undefined };
    const configured = configuredVariants();
    const items = body.items.slice(0, 100).map((item) => ({
      id: String(item.variant_id || item.id || ''),
      quantity: Number(item.quantity || 0),
      markers: itemMarker(item),
      nested: Boolean(item.parent_relationship?.parent_key || item.parent_relationship?.parent),
    }));
    return {
      shape: 'items',
      count: body.items.length,
      itemCount: Number.isFinite(Number(body.item_count)) ? Number(body.item_count) : undefined,
      total: Number.isFinite(Number(body.total_price)) ? Number(body.total_price) : undefined,
      decorated: items.filter((item) => item.markers.length > 0).length,
      unmarkedConfiguredIds: items.filter((item) => configured.has(item.id) && item.markers.length === 0).map((item) => item.id),
      items,
    };
  };

  const payloadSummary = (body) => {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      let markerFields = 0;
      let ids = 0;
      let parents = 0;
      let fields = 0;
      for (const [name] of body.entries()) {
        fields += 1;
        if (name.includes('_theme_upsell_key')) markerFields += 1;
        if (/(?:^|\])\[id\]$|^id$/.test(name)) ids += 1;
        if (name.includes('parent_id') || name.includes('parent_line_key')) parents += 1;
      }
      return { shape: body instanceof FormData ? 'form-data' : 'url-search-params', fields, ids, parents, markerFields };
    }
    if (typeof body === 'string') {
      if (body.length > BODY_LIMIT) return { shape: 'text', bodyInspection: 'size-limit' };
      try { return payloadSummary(JSON.parse(body)); } catch (_) { return { shape: 'text', bodyInspection: 'not-json' }; }
    }
    if (body && typeof body === 'object' && Array.isArray(body.items)) {
      return {
        shape: 'json-items',
        ids: body.items.map((item) => String(item.id || '')).filter(Boolean),
        parents: body.items.filter((item) => item.parent_id || item.parent_line_key).length,
        markerItems: body.items.filter((item) => item.properties && item.properties._theme_upsell_key).length,
      };
    }
    return { shape: body ? 'other' : 'empty' };
  };

  const describeUpsell = (node) => {
    const form = node?.querySelector('product-form form');
    const markerFields = form ? [...new FormData(form).keys()].filter((key) => key.includes('_theme_upsell_key')).length : 0;
    return {
      key: node?.dataset.upsellKey || '',
      id: node?.dataset.id || '',
      style: node?.dataset.style || '',
      toggle: node?.dataset.toggle === 'true',
      selected: node?.dataset.selected === 'true',
      formPresent: Boolean(form),
      markerFields,
      formSubmitter: Boolean(form?.querySelector('[type="submit"]')),
    };
  };
  const describeForm = (form) => {
    const host = form?.closest('product-form');
    const upsell = host?.closest('cart-drawer-upsell');
    const keys = form ? [...new FormData(form).keys()] : [];
    return {
      upsell: Boolean(upsell),
      upsellKey: upsell?.dataset.upsellKey || '',
      upsellId: upsell?.dataset.id || '',
      cartUpsellAttribute: host?.dataset.isCartUpsell === 'true',
      markerFields: keys.filter((key) => key.includes('_theme_upsell_key')).length,
      hasId: keys.includes('id'),
      hasQuantity: keys.includes('quantity'),
      hasSellingPlan: keys.includes('selling_plan'),
      submitter: form?.ownerDocument?.activeElement?.getAttribute?.('type') || '',
    };
  };
  const describeProductUpsell = (node, formIds) => ({
    key: node?.dataset.upsellKey || '',
    id: node?.dataset.id || '',
    formId: node?.dataset.form || '',
    selected: node?.dataset.selected === 'true',
    variantAvailable: node?.dataset.variantAvailable === 'true',
    insideProductInfo: Boolean(node?.closest('product-info')),
    formMatch: formIds.has(node?.dataset.form || ''),
  });
  const productUpsells = () => {
    const formIds = new Set([...document.querySelectorAll('product-form form')].map(form => form.id).filter(Boolean));
    return [...document.querySelectorAll('product-info-upsell[data-form]')].map(node => describeProductUpsell(node, formIds));
  };
  const describeProductForm = (host) => {
    const form = host?.querySelector('form');
    const selected = productUpsells().filter(upsell => upsell.formId === form?.id && upsell.selected);
    return {
      formId: form?.id || '',
      dbCommerce: Boolean(window.DBCommerce),
      skipCart: host?.dataset.skipCart === 'true',
      v6Bundle: host?.dataset.v6Bundle === 'true',
      includePdpUpsells: host?.dataset.includePdpUpsells === 'true',
      selectedProductUpsellCount: selected.length,
      selectedProductUpsellIds: selected.map(upsell => upsell.id),
    };
  };

  const startRequest = (meta, method, transport, payload) => {
    const request = { id: `u${++sequence}`, started: time(), token: session };
    pending.set(request.id, request);
    log('request.start', { request: request.id, route: meta, method, transport, payload, correlation: 'submit-and-time-window' });
    return request;
  };
  const finishRequest = (request, status, detail = {}) => {
    if (!active || request.token !== session) return;
    pending.delete(request.id);
    log('request.result', {
      request: request.id,
      status,
      duration: time() - request.started,
      ...detail,
    }, status >= 400 || detail.response?.unmarkedConfiguredIds?.length ? 'error' : 'info');
    if (detail.response?.unmarkedConfiguredIds?.length) {
      log('fault.upsell-unmarked-cart-line', {
        request: request.id,
        ids: detail.response.unmarkedConfiguredIds,
        reason: 'configured cart upsell variant returned without a decorative marker',
      }, 'error');
    }
    scheduleScan();
  };

  const installFetch = () => {
    if (typeof window.fetch !== 'function') return;
    originalFetch = window.fetch;
    const epoch = session;
    fetchWrapper = function wrappedFetch(...args) {
      const input = args[0];
      const options = args[1] || {};
      const meta = route(input);
      const tracked = active && epoch === session && meta.sameOrigin && meta.kind.startsWith('cart:');
      const request = tracked ? startRequest(meta, options.method || (input instanceof Request ? input.method : 'GET'), 'fetch', payloadSummary(options.body)) : null;
      const promise = Reflect.apply(originalFetch, this, args);
      if (request) {
        Promise.resolve(promise).then(async (response) => {
          let body;
          try { body = await response.clone().json(); } catch (_) { body = null; }
          finishRequest(request, response.status, { response: responseSummary(body) });
        }, () => finishRequest(request, 0, { failure: 'network-rejection' })).catch(() => {});
      }
      return promise;
    };
    window.fetch = fetchWrapper;
  };

  const installXhr = () => {
    if (!window.XMLHttpRequest) return;
    xhrOpen = XMLHttpRequest.prototype.open;
    xhrSend = XMLHttpRequest.prototype.send;
    openWrapper = function wrappedOpen(method, url, ...args) {
      const result = Reflect.apply(xhrOpen, this, [method, url, ...args]);
      safe(() => xhrRequests.set(this, { meta: route(url), method }), undefined);
      return result;
    };
    sendWrapper = function wrappedSend(body) {
      const info = xhrRequests.get(this);
      const tracked = active && info?.meta?.sameOrigin && info.meta.kind.startsWith('cart:');
      const request = tracked ? startRequest(info.meta, info.method, 'xhr', payloadSummary(body)) : null;
      const onLoadEnd = () => {
        let bodyJson = null;
        safe(() => {
          if (this.responseType === 'json') bodyJson = this.response;
          else if (!this.responseType && String(this.responseText || '').length <= BODY_LIMIT) bodyJson = JSON.parse(this.responseText);
        }, null);
        if (request) finishRequest(request, this.status, { response: responseSummary(bodyJson) });
      };
      if (request) this.addEventListener('loadend', onLoadEnd, { once: true });
      try { return Reflect.apply(xhrSend, this, [body]); }
      catch (error) { if (request) finishRequest(request, 0, { failure: 'synchronous-throw' }); throw error; }
    };
    XMLHttpRequest.prototype.open = openWrapper;
    XMLHttpRequest.prototype.send = sendWrapper;
  };

  const snapshot = () => ({
    drawers: [...document.querySelectorAll('cart-drawer')].map((drawer) => ({
      open: drawer.classList.contains('active'),
      empty: drawer.classList.contains('is-empty'),
      rows: drawer.querySelectorAll('.cart-item').length,
      emptyMarkup: Boolean(drawer.querySelector('.drawer__inner-empty')),
    })),
    upsells: [...document.querySelectorAll('cart-drawer-upsell')].map(describeUpsell),
    express: [...document.querySelectorAll('product-upsell-express')].map((host) => ({
      configCount: document.querySelectorAll('cart-drawer [data-express-upsell-config]').length,
      fieldNames: [...host.querySelectorAll('[data-express-fields] input')].map(input => input.name),
      itemIds: [...host.querySelectorAll('[data-express-fields] input')].filter(input => input.name.endsWith('[id]')).map(input => input.value),
      blocked: Boolean(host.querySelector('[data-product-wallet]')?.inert),
      walletHidden: Boolean(host.querySelector('[data-db-wallet]')?.hidden)
    })),
    productUpsells: productUpsells(),
    productForms: [...document.querySelectorAll('product-form')].map(describeProductForm),
    cartRows: [...document.querySelectorAll('cart-drawer .cart-item')].map((row) => ({
      id: row.id,
      upsellKey: row.dataset.themeUpsellKey || '',
      additionalKey: row.dataset.themeAdditionalKey || '',
      giftKey: row.dataset.themeGiftKey || '',
      addonKey: row.dataset.dbAddonKey || '',
      nested: row.classList.contains('cart-item__nested-line'),
    })),
  });
  const scan = () => {
    if (!active) return;
    const state = snapshot();
    const signature = JSON.stringify(state);
    if (signature !== scan.previous) { log('ui.snapshot', state); scan.previous = signature; }
    if (state.drawers.length > 1) once('duplicate-drawer', 'fault.multiple-cart-drawers', { count: state.drawers.length }, 'error');
    state.upsells.forEach((upsell) => {
      if (upsell.formPresent && !upsell.markerFields) {
        once(`missing-marker:${upsell.key}`, 'fault.upsell-form-marker-missing', upsell, 'error');
      }
    });
    state.productUpsells.forEach((upsell) => {
      if (!upsell.insideProductInfo) once(`product-outside:${upsell.key}`, 'fault.product-upsell-outside-product-info', upsell, 'error');
      if (!upsell.formMatch) once(`product-form-mismatch:${upsell.key}`, 'fault.product-upsell-form-mismatch', upsell, 'error');
      if (upsell.selected && (!upsell.id || !upsell.variantAvailable)) once(`product-invalid:${upsell.key}`, 'fault.product-upsell-selected-invalid', upsell, 'error');
    });
    state.drawers.forEach((drawer) => {
      if (drawer.open && !drawer.rows && !drawer.emptyMarkup && !pending.size) once('blank-drawer', 'warning.drawer-no-content', { drawer }, 'warn');
    });
  };
  const scheduleScan = () => {
    if (!active || scanTimer) return;
    scanTimer = setTimeout(() => { scanTimer = null; safe(scan); }, 150);
  };
  const listen = (target, type, handler, options) => {
    const wrapped = (event) => safe(() => handler(event));
    target.addEventListener(type, wrapped, options);
    cleanup.push(() => target.removeEventListener(type, wrapped, options));
  };

  const start = () => {
    if (active) return api;
    active = true;
    session += 1;
    seen = new Set();
    scan.previous = null;
    log('session.start', { version: VERSION, designMode: Boolean(window.Shopify?.designMode), coverage: 'cart-upsell-submit-request-response-render' });
    listen(document, 'click', (event) => {
      if (event.target.closest?.('product-upsell-express')) log('express.click', { express: snapshot().express });
      const control = event.target.closest?.('cart-drawer-upsell .upsell-toggle-btn, cart-drawer-upsell [type="submit"]');
      if (control) log('upsell.click', { control: control.className || control.type || control.localName, upsell: describeUpsell(control.closest('cart-drawer-upsell')) });
      scheduleScan();
    }, true);
    listen(document, 'submit', (event) => {
      const form = event.target.closest?.('form');
      const host = form?.closest('product-form');
      if (!host?.closest('cart-drawer-upsell')) {
        if (host) {
          log('product.submit.capture', { ...describeProductForm(host), preventedAtCapture: event.defaultPrevented });
          queueMicrotask(() => log('product.submit.after', { ...describeProductForm(host), preventedAfterHandlers: event.defaultPrevented }));
        }
        return;
      }
      log('upsell.submit.capture', { ...describeForm(form), preventedAtCapture: event.defaultPrevented });
      queueMicrotask(() => log('upsell.submit.after', { ...describeForm(form), preventedAfterHandlers: event.defaultPrevented }));
    }, true);
    listen(document, 'db-commerce:debug', (event) => {
      const detail = event.detail || {};
      const type = String(detail.event || 'event').slice(0, 80);
      const level = ['error', 'warn', 'info'].includes(detail.level) ? detail.level : 'info';
      const { event: ignoredEvent, level: ignoredLevel, ...payload } = detail;
      log(`commerce.${type}`, payload, level);
      scheduleScan();
    }, true);
    ['cart:updated', 'cart:error', 'cart-drawer:open', 'cart-drawer:close'].forEach((type) => listen(document, type, (event) => {
      log(`event.${type.replace(':', '.')}`, { source: event.detail?.source || '', hasCart: Boolean(event.detail?.cart) });
      scheduleScan();
    }, true));
    ['shopify:section:load', 'shopify:section:unload', 'shopify:block:select', 'shopify:block:deselect'].forEach((type) => listen(document, type, (event) => log('editor.lifecycle', { event: type, target: event.target?.id || event.target?.localName || '' }), true));
    listen(window, 'error', (event) => log('script.error', { message: String(event.message || '').slice(0, 160), file: route(event.filename).host }, 'error'), true);
    listen(window, 'unhandledrejection', (event) => log('promise.unhandled', { reason: String(event.reason?.message || event.reason || '').slice(0, 160) }, 'error'));
    observer = new MutationObserver(() => { log('dom.batch', { reason: 'cart-upsell-dom-change' }); scheduleScan(); });
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-selected', 'data-id', 'checked', 'disabled', 'hidden'] });
    installFetch();
    installXhr();
    scan();
    return api;
  };
  const stop = () => {
    if (!active) return;
    log('session.stop', { pending: pending.size });
    active = false;
    session += 1;
    observer?.disconnect();
    clearTimeout(scanTimer);
    scanTimer = null;
    cleanup.splice(0).forEach((remove) => safe(remove));
    pending.clear();
    if (window.fetch === fetchWrapper) window.fetch = originalFetch;
    if (window.XMLHttpRequest) {
      if (XMLHttpRequest.prototype.open === openWrapper) XMLHttpRequest.prototype.open = xhrOpen;
      if (XMLHttpRequest.prototype.send === sendWrapper) XMLHttpRequest.prototype.send = xhrSend;
    }
  };
  const report = () => ({ version: VERSION, active, discarded, records: records.slice(), latest: snapshot(), limitations: [
    '只观察同源 cart Ajax 请求、购物车附加销售表单和可见 DOM；不读取客户输入、Cookie、令牌或原始请求正文。',
    '时间窗口和同一提交上下文是关联线索，不是浏览器内部因果证明。',
    '未观察到错误不等于功能验收通过；折扣、库存、结账和实际订单结果仍需商店操作确认。',
  ] });
  const download = () => {
    const blob = new Blob([JSON.stringify(report(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `legacy-upsell-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const api = Object.freeze({ version: VERSION, start, stop, scan: () => safe(scan), report, download, clear: () => { records = []; discarded = 0; seen = new Set(); } });
  Object.defineProperty(window, 'LegacyUpsellDebug', { value: api, configurable: true });
  const params = new URLSearchParams(location.search);
  if (params.get('theme_debug') === '1' || params.get('upsell_debug') === '1') start();
})();
